import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type AbilitiesService from 'ember-can/services/abilities';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type LocationsService from 'garaje/services/locations';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { AnalyticsEventNames } from 'garaje/utils/enums';

import type AnalyticsParkingController from './controller';

export interface AnalyticsParkingRouteParams {
  location_id: string;
}

export default class AnalyticsParkingRoute extends Route {
  @service declare featureConfig: FeatureConfigService;
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare metrics: MetricsService;
  @service declare locations: LocationsService;

  title = 'Parking · Analytics · Envoy';

  queryParams = {
    location_id: { refreshModel: true },
  };

  beforeModel(): void {
    if (this.abilities.cannot('visit parking in analytic')) {
      this.router.transitionTo('unauthorized');
    }
  }

  afterModel(): void {
    this.#logMetrics();
  }

  setupController(controller: AnalyticsParkingController, model: void, transition: Transition): void {
    super.setupController(controller, model, transition);

    const { location_id } = <AnalyticsParkingRouteParams>this.paramsFor(this.routeName);
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const protectedController = this.controllerFor('protected');

    if (location_id && this.state.currentLocation.id != location_id) {
      protectedController.send('switchLocation', location_id, 'analytics.parking');

      this.locations.loadAllTask
        .perform()
        .then(() => {
          const matchingLocation = this.locations.allActive.filter((entry) => entry.id === location_id);
          if (matchingLocation.length === 0) {
            this.router.transitionTo('unauthorized');
          }
        })
        .catch((error) => {
          throw error;
        });
    }

    if (this.abilities.can('visit parking in analytic')) {
      void controller.fetchSigmaUrlTask.perform();
    }
  }

  #logMetrics(): void {
    const {
      currentCompany: { id: companyId },
      currentLocation: { id: locationId },
    } = this.state;
    const details = {
      companyId,
      locationId,
      app: this.state.workplaceSubscription?.app,
      plan: this.state?.workplaceSubscription?.plan,
      userId: this.currentAdmin?.id || '',
      employeeId: this.currentAdmin.employee?.id || '',
    };
    this.metrics.trackEvent(AnalyticsEventNames.PARKING_DASHBOARD_VISITED, details);
    this.metrics.logMonitorEvent(AnalyticsEventNames.PARKING_DASHBOARD_VISITED, details);
  }
}
